/**
 * @fileoverview Curated Lucide glyph catalog and icon component registry.
 * @module ui-artwork/catalog/glyphs
 *
 * Responsibilities: Register common glyph IDs, aliases, and Lucide components.
 * Dependencies: lucide-react.
 * Architecture: Runtime vector components are rendered to inline SVG by the builder.
 * Notes: Valid Lucide names outside the curated set resolve dynamically as glyphs.
 */
import {
  Activity, AlertTriangle, BookOpen, Briefcase, Building, Building2, Calendar,
  CalendarPlus, Check, ChevronDown, ChevronRight, Circle, CircleAlert, CircleCheck,
  CircleX, Clipboard, ClipboardCheck, ClipboardList, Clock, Droplets, Edit,
  FileCheck, FilePen, FileText, Flame, FolderCheck, FolderOpen, FolderPlus, Hash,
  Home, House, LandPlot, Layers, Loader, Mail, Map, MapPin, PenTool, Plus, Search,
  Send, Settings, Shield, ShieldCheck, SquareCheck, Star, Tag, Trash, TreeDeciduous,
  Trees, Truck, TriangleAlert, User, Users, Wrench, X, type LucideIcon,
} from 'lucide-react';
import type { ArtworkCatalogEntry } from '../types';

export const LUCIDE_COMPONENTS: Record<string, LucideIcon> = {
  activity: Activity, 'alert-circle': CircleAlert, 'alert-triangle': AlertTriangle,
  'book-open': BookOpen, briefcase: Briefcase, building: Building, 'building-2': Building2,
  calendar: Calendar, 'calendar-plus': CalendarPlus, 'check-circle': CircleCheck,
  'check-circle-2': CircleCheck, 'check-square': SquareCheck, circle: Circle,
  check: Check, 'chevron-down': ChevronDown, 'chevron-right': ChevronRight,
  'clipboard-check': ClipboardCheck, 'clipboard-list': ClipboardList,
  clipboard: Clipboard, clock: Clock, droplets: Droplets, edit: Edit,
  'file-check': FileCheck, 'file-edit': FilePen, 'file-text': FileText,
  flame: Flame, 'folder-check': FolderCheck, 'folder-open': FolderOpen,
  'folder-plus': FolderPlus, hash: Hash, home: Home, house: House,
  'land-plot': LandPlot, layers: Layers, loader: Loader, mail: Mail, map: Map,
  'map-pin': MapPin, 'pen-tool': PenTool, plus: Plus, search: Search, send: Send,
  settings: Settings, shield: Shield, 'shield-check': ShieldCheck, star: Star,
  tag: Tag, tool: Wrench, trash: Trash, trees: Trees, truck: Truck,
  tree: TreeDeciduous, 'triangle-alert': TriangleAlert, user: User, users: Users, wrench: Wrench,
  x: X, 'x-circle': CircleX,
};

const pascal = (name: string) => name.split('-').map((part) => part[0].toUpperCase() + part.slice(1)).join('');
export const GLYPH_ARTWORK: ArtworkCatalogEntry[] = Object.keys(LUCIDE_COMPONENTS).map((name) => ({
  id: `glyph:${name}`,
  legacyAliases: [pascal(name)],
  label: pascal(name).replace(/([a-z])([A-Z])/g, '$1 $2'),
  category: 'ui',
  style: 'glyph',
  keywords: [name.replace(/-/g, ' '), pascal(name).toLowerCase()],
  colorPolicy: 'customizable',
  defaultColor: '#475569',
  source: 'lucide-glyph',
  attribution: 'Lucide, ISC License',
}));

export function resolveLucideName(idOrAlias: string): string | undefined {
  const normalized = idOrAlias.trim().replace(/^glyph:/i, '');
  const kebab = normalized
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
  return LUCIDE_COMPONENTS[kebab] ? kebab : undefined;
}
